import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { AppStates } from '../../../types';
import { useConfiguratorDispatch } from '../../../store';

import { ProductPreviewComponent } from '../../preview';
import { CartItemState } from '../../app/Configurator';
import { useMaterials } from '../../clothingType/Materials.hooks';

import {
  addUpsell,
  selectAvailableUpsellItems,
  selectChosenUpsellItems
} from '../store/upsellsSlice';

import { UpsellSelector } from './UpsellSelector';
import { ViewCartButton } from '../../app/components/ViewCartButton';
import { ContinueShoppingButton } from '../../app/components/ContinueShoppingButton';
import { PopularAddonsHeader } from '../../../components/headers/PopularAddonsHeader';
import { BackToConfiguratorButton } from './BackToConfiguratorButton';
import { AddToCartButton } from '../../cart/AddToCartButton';

interface UpsellsV2Props {
  isReadyForCart: boolean;
  isMobileView: boolean;
  preSelectedUpsells: CartItemState[];
  cartItemId?: string;
  setAppState: Dispatch<SetStateAction<AppStates>>;
  baseProductName?: string;
  baseProductCartItemId?: string;
  isUpdate?: boolean;
}

// Confirmation message component
function ConfirmationMessage({
  productName,
  isUpdate
}: {
  productName: string;
  isUpdate?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-[#6BDAD5] bg-[#6BDAD5] p-2.5">
      <div className="flex-shrink-0">
        <svg
          className="h-6 w-6 text-[#004876]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12l2 2 4-4"
          />
        </svg>
      </div>
      <div className="flex-1">
        <p className="text-base font-semibold text-[#004876]">
          {isUpdate
            ? 'Love the update! Your refreshed pick is all set.'
            : `Pop! Your ${productName} just bounced into your cart!`}
        </p>
      </div>
    </div>
  );
}

export function UpsellsV2({
  isReadyForCart,
  isMobileView,
  preSelectedUpsells,
  baseProductName = 'Product',
  baseProductCartItemId,
  isUpdate = false,
  cartItemId,
  setAppState
}: UpsellsV2Props) {
  console.log('[UpsellsV2] Received baseProductName:', baseProductName);
  const dispatch = useConfiguratorDispatch();
  const upsellOptions = useSelector(selectAvailableUpsellItems);
  const selectedUpsells = useSelector(selectChosenUpsellItems);
  const { materials, isLoading } = useMaterials();
  const preSelectedUsed = useRef(false);
  const [addedUpsells, setAddedUpsells] = useState<Set<string>>(new Set());
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isMobileView && topRef.current) {
      topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const stickyHeader = document.querySelector<HTMLElement>(
        '#header__stick, .header__main, .header.wrapper'
      );
      const fullHeader = document.querySelector<HTMLElement>(
        'header'
      );
      const body = document.querySelector<HTMLElement>('body');
      if (body && body.scrollTop) {
        console.log('Scrolled to top', body, fullHeader?.scrollHeight, stickyHeader?.offsetHeight );
        body.scrollTop = (fullHeader?.scrollHeight || 0) - (stickyHeader?.offsetHeight || 0);
      }
      // const headerHeight = stickyHeader?.offsetHeight ?? 0;
      // const elementTop =
      //   topRef.current.getBoundingClientRect().top + window.scrollY;
      // window.scrollTo({ top: elementTop - headerHeight - 8, behavior: 'smooth' });
    }
  }, [isMobileView]);

  useEffect(() => {
    if (isLoading || preSelectedUsed.current) {
      return;
    }
    preSelectedUsed.current = true;

    const params = new URLSearchParams(window.location.search);
    const legacyUpsellShopifyVariantIds = params
      .getAll('upsell')
      .map(key => parseInt(key.split(':')[0]));
    for (const variantId of legacyUpsellShopifyVariantIds) {
      const upsell = upsellOptions.find(u => u.shopify_id === variantId);
      if (upsell) dispatch(addUpsell({ upsell }));
    }

    for (const preSelectedUpsell of preSelectedUpsells) {
      const upsell = upsellOptions.find(
        u => u.product_id === preSelectedUpsell.product_id
      );

      const isUpsellAlreadyChosen = selectedUpsells.some(
        u => u.upsell.product_id === preSelectedUpsell.product_id
      );

      if (!upsell || isUpsellAlreadyChosen) continue;

      const material = materials.find(
        material =>
          material?.material_id ===
          preSelectedUpsell.label_slots?.[0]?.material_id
      );

      dispatch(
        addUpsell({
          cartItemId: preSelectedUpsell.cart_item_id,
          upsell,
          material
        })
      );

      // Mark pre-selected upsells as added so they show "Item Added To Cart!"
      setAddedUpsells(prev => new Set(prev).add(upsell.product_id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, upsellOptions, materials, preSelectedUpsells, dispatch]);

  const handleUpsellAdded = (productId: string) => {
    setAddedUpsells(prev => new Set(prev).add(productId));
  };

  return (
    <>
      <div ref={topRef} className="flex flex-col gap-5">
        {/* Confirmation Message */}
        <ConfirmationMessage
          productName={baseProductName}
          isUpdate={isUpdate}
        />

        <PopularAddonsHeader
          title="Popular Add-ons"
          description="Pop these in too — special add-on price!"
        />

        {isMobileView && (
          <div className="h-0 w-0 overflow-hidden">
            <ProductPreviewComponent />
          </div>
        )}

        {upsellOptions?.map(upsell => (
          <UpsellSelector
            key={upsell.product_id}
            upsell={upsell}
            immediateAddToCart={!isUpdate}
            isAdded={addedUpsells.has(upsell.product_id)}
            onUpsellAdded={() => handleUpsellAdded(upsell.product_id)}
            baseProductCartItemId={baseProductCartItemId}
          />
        ))}
      </div>
      {isUpdate ? (
        <div className="flex gap-3">
          <BackToConfiguratorButton
            style={{ flexGrow: 1 }}
            setAppState={setAppState}
          />
          <AddToCartButton
            style={{ flexGrow: 1 }}
            setAppState={setAppState}
            isReadyForCart={isReadyForCart}
            cartItemId={cartItemId}
            preSelectedUpsells={preSelectedUpsells}
            customButtonText="Save & View Cart"
            onComplete={() => {
              window.location.href = '/cart';
            }}
          />
        </div>
      ) : (
        <div className="p-t-20 flex flex-col gap-5">
          <ContinueShoppingButton />
          <ViewCartButton />
        </div>
      )}
    </>
  );
}
